import clsx from 'clsx';
import { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { hexToRgb } from '@vestico/utils';

import {
  WidgetBubbleLayoutConfigurationDefaults,
  WidgetCommonLayoutConfiguration,
  WidgetItemAspectRatio,
  WidgetStoriesLayoutConfiguration,
} from '@vestico/model';

import { getIsRTL } from '@vestico/i18n';

import { CustomCSSProperties } from '../../../model';
import { LayoutLoadMoreHandler } from '../carousel/LayoutLoadMoreHandler';
import { CarouselItem } from '../common/CarouselItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import style from './StoriesLayout.module.css';

export interface StoriesLayoutProps extends BaseLayoutProps {
  layout: WidgetStoriesLayoutConfiguration & WidgetCommonLayoutConfiguration;
}

export const StoriesLayout = ({ layout, responses, onResponseClick, loading, loadMore, hasMore }: StoriesLayoutProps) => {
  const itemSize = useMemo(
    () => (layout.itemSize && !Number.isNaN(layout.itemSize) ? layout.itemSize : WidgetBubbleLayoutConfigurationDefaults.itemSize),
    [layout]
  );

  const itemStyle = useMemo(() => layout.style ?? 'round', [layout.style]);
  const itemBorderWidth = useMemo(() => layout.itemBorderWidth ?? WidgetBubbleLayoutConfigurationDefaults.itemBorderWidth, [layout.itemBorderWidth]);
  const itemPadding = useMemo(() => layout.itemPadding ?? WidgetBubbleLayoutConfigurationDefaults.itemPadding, [layout.itemPadding]);
  const itemShadowEnabled = useMemo(
    () => (layout.itemShadowEnabled !== undefined ? layout.itemShadowEnabled : WidgetBubbleLayoutConfigurationDefaults.itemShadowEnabled === 1),
    [layout.itemShadowEnabled]
  );
  const scrollBackgroundColor = useMemo(() => {
    const hexValue = layout.scrollBackgroundColor ?? WidgetBubbleLayoutConfigurationDefaults.scrollBgColor;
    const { red, green, blue } = hexToRgb(hexValue);
    return `${red}, ${green}, ${blue}`;
  }, [layout.scrollBackgroundColor]);

  const aspectRatio: WidgetItemAspectRatio = useMemo(
    () => (itemStyle === 'square' ? layout.aspectRatio ?? 'square' : 'square'),
    [itemStyle, layout.aspectRatio]
  );

  const handleResponseClick = useCallback((index: number) => onResponseClick(index), [onResponseClick]);

  const [isScrolledToStart, setScrolledToStart] = useState(true);
  const [isScrolledToEnd, setScrolledToEnd] = useState(false);

  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses, targetIndex: isScrolledToStart ? 0 : 1 });
  const onUpdateInView = useCallback((index: number) => (inView: boolean) => updateInView(index, inView), [updateInView]);

  const customCSS = useMemo(() => {
    const css: CustomCSSProperties = {};
    css['--vw-rowlayout-item-padding'] = `${itemPadding}px`;
    css['--vw-stories-bg-color'] = scrollBackgroundColor;
    return css as CSSProperties;
  }, [itemPadding, scrollBackgroundColor]);

  const { styles, borderRadiusStyles } = useCarouselStyles({
    borderRadius: itemStyle === 'round' ? '50%' : layout.itemBorderRadius,
    itemSize,
    aspectRatio,
    customCSS,
  });

  const responseMarkups = useMemo(() => {
    if (responses.length === 0) {
      return [];
    }

    const finalChildren = responses.map((currentResponse, index) => {
      return (
        <CarouselItem
          key={currentResponse.postDocumentID}
          index={index}
          response={currentResponse}
          onClick={handleResponseClick}
          hideVideoIcon
          customCSS={{
            borderWidth: itemBorderWidth,
            filter: `${!itemShadowEnabled ? 'none' : 'drop-shadow(var(--overlay-text-shadow))'}`,
            borderColor: 'var(--color)',
            borderStyle: 'solid',
          }}
          aspectRatio={aspectRatio}
          updateInView={onUpdateInView(index)}
          playVideo={currentResponse === firstInViewVideoResponse}
          borderRadius={borderRadiusStyles}
        />
      );
    });

    const loadMoreHandler = hasMore ? <LayoutLoadMoreHandler loading={loading} loadMore={loadMore} isItem /> : null;
    return [...finalChildren, loadMoreHandler];
  }, [
    responses,
    hasMore,
    loading,
    loadMore,
    handleResponseClick,
    itemBorderWidth,
    itemShadowEnabled,
    aspectRatio,
    onUpdateInView,
    firstInViewVideoResponse,
    borderRadiusStyles,
  ]);

  const isRTL = getIsRTL();

  const nodeRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const { current } = nodeRef;
    if (!current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = current;

    // We invert scrollLeft when in RTL mode
    const adjustedScrollLeft = isRTL ? -scrollLeft : scrollLeft;
    const scrolledToStart = Math.floor(adjustedScrollLeft) <= 0;
    const scrolledToEnd = adjustedScrollLeft >= scrollWidth - clientWidth;

    setScrolledToStart(scrolledToStart);
    setScrolledToEnd(scrolledToEnd);
  }, [nodeRef, nodeRef.current, isRTL]);

  useEffect(handleScroll, [handleScroll]);

  return (
    <div className={clsx(style.root, { [style.rtl]: isRTL })} style={styles}>
      <div className={clsx(style.content)} ref={nodeRef} onScroll={handleScroll}>
        {responseMarkups}
      </div>
      <div className={clsx(style.overlay, style.start, { [style.rtl]: isRTL, [style.visible]: !isScrolledToStart })} />
      <div className={clsx(style.overlay, style.end, { [style.rtl]: isRTL, [style.visible]: !isScrolledToEnd })} />
    </div>
  );
};
